import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { State } from '../../models/state.model';
import { Municipality } from '../../models/municipality.model';
import { CommonModule } from '@angular/common';
import { ConfirmModalComponent } from '../../components/modals/confirm-modal/confirm-modal.component';
import { StatesService } from '../../services/states.service';
import { ToastService } from '../../services/toast.service';
import { PreRegistrationService } from '../../services/pre-registration.service';

@Component({
  selector: 'app-pre-registration',
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    ConfirmModalComponent
  ],
  templateUrl: './pre-registration.component.html',
  styles: `
    .disabled-icon {
      opacity: 0.3;
    }

    .vertical-line {
      border-left: 2px solid #ccc;
      height: 1rem;
      margin: 0 1.2rem;
    }

    .fade-in {
      animation: fadeIn 300ms ease-in-out;
    }

    @keyframes fadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `
})
export class PreRegistrationComponent implements OnInit, AfterViewInit {
  step = 1;
  loading = false;
  formGroupOne!: FormGroup;
  formGroupTwo!: FormGroup;
  states: State[] = [];
  municipalities: Municipality[] = [];
  imageSrc?: string | null;
  user = JSON.parse(localStorage.getItem('user') ?? 'null');

  progressList = [
    {
      title: 'Paso 1',
      subtitle: 'Instrucciones',
      icon: 'signpost',
      step: 1
    },
    {
      title: 'Paso 2',
      subtitle: 'Tus detalles',
      icon: 'card_text',
      step: 2
    },
    {
      title: 'Paso 3',
      subtitle: 'Datos de contacto',
      icon: 'person_lines_fill',
      step: 3
    },
    {
      title: 'Paso 4',
      subtitle: 'Inscripción finalizada',
      icon: 'check2_circle',
      step: 4
    }
  ];

  programs = [
    {
      id: 18,
      name: 'Cultura Física'
    },
    {
      id: 8,
      name: 'Comunicación Deportiva'
    },
    {
      id: 14,
      name: 'Administración y Gestión de Centros de Instituciones Deportivas'
    }
  ];

  sports = ['Futbol', 'Béisbol', 'Boxeo', 'Basquetbol'];
  genders = ['Hombre', 'Mujer', 'No Binario'];

  private router = inject(Router);
  private statesService = inject(StatesService);
  private preRegistrationService = inject(PreRegistrationService);
  private route = inject(ActivatedRoute);
  private toast = inject(ToastService);

  ngOnInit() {
    this.checkStep();
    this.getStates();
    this.setFormOne();
    this.setFormTwo();
  }

  ngAfterViewInit() {
    this.scrollIntoView();
  }

  setFormOne() {
    this.formGroupOne = new FormGroup({
      first_name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20)
      ]),
      last_name_father: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20)
      ]),
      last_name_mother: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20)
      ]),
      gender: new FormControl('', Validators.required),
      birth_date: new FormControl('', Validators.required),
      state_of_origin: new FormControl('', Validators.required),
      municipality_origin: new FormControl(
        { value: '', disabled: true },
        Validators.required
      )
    });
  }

  setFormTwo() {
    this.formGroupTwo = new FormGroup({
      favorite_sport: new FormControl('', Validators.required),
      contact_email: new FormControl('', [
        Validators.required,
        Validators.email
      ]),
      contact_phone: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10)
      ]),
      education_program_id: new FormControl(0, Validators.required),
      tutor_authorization: new FormControl(false, Validators.requiredTrue)
    });
  }

  checkStep() {
    this.route.queryParams.subscribe((params) => {
      if (this.user) {
        this.updateRouterNavigate(3);
      } else if (params['paso']) {
        const number = parseInt(params['paso'], 10);

        if (number === 2 || number === 3) {
          this.updateRouterNavigate(number);
        } else {
          this.router.navigate([], { relativeTo: this.route });
        }
      } else {
        this.router.navigate([], { relativeTo: this.route });
        this.clearLocalStorage();
      }
    });
  }

  clearLocalStorage() {
    localStorage.removeItem('user');
    this.user = null;
  }

  updateRouterNavigate(step: number) {
    this.step = step;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { paso: step }
    });

    this.scrollIntoView();
  }

  getStates() {
    this.statesService.getStates().subscribe({
      next: (res) => {
        this.states = res.data;
      }
    });
  }

  goToStep(step: number) {
    this.step = step;
    this.scrollIntoView();

    if (this.step === 0) {
      this.router.navigate([], { relativeTo: this.route });
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { paso: step }
    });
  }

  scrollIntoView() {
    const element = document.getElementById('step-' + this.step);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', inline: 'start' });
      scrollTo(0, 0);
    }
  }

  getMunicipalities(event: any, form: number) {
    const state = event.target.value;
    const stateId = this.states.find((s) => s.name === state)!.id;
    this.statesService.getMunicipalities(stateId).subscribe({
      next: (res) => {
        this.municipalities = res.data[0].municipalities;
        if (form === 1) {
          this.formGroupOne.get('municipality_origin')?.enable();
          this.formGroupOne.get('municipality_origin')?.setValue('');
        } else if (form === 2) {
          this.formGroupTwo.get('municipality_origin')?.enable();
          this.formGroupTwo.get('municipality_origin')?.setValue('');
        }
      }
    });
  }

  resetFormOne() {
    this.formGroupOne.reset();
    this.formGroupOne.get('municipality_origin')?.disable();
    this.formGroupOne.get('municipality_origin')?.setValue('');
    this.formGroupOne.get('state_of_origin')?.setValue('');
  }

  saveUser() {
    this.user = this.formGroupOne.value;
    localStorage.setItem('user', JSON.stringify(this.formGroupOne.value));
    this.goToStep(3);
    this.resetFormOne();
  }

  sendData() {
    this.loading = true;
    const formTwo = this.formGroupTwo.value;

    const body = {
      ...this.user,
      ...formTwo,
      education_program_id: parseInt(formTwo.education_program_id, 10)
    };

    this.preRegistrationService.create(body).subscribe({
      next: (res) => {
        console.log(res);
        this.loading = false;
        this.toast.success('Inscripción completada');
        this.clearLocalStorage();
        this.goToStep(4);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  checkValidation(form: number, field: string): boolean {
    if (form === 1) {
      return (
        this.formGroupOne.get(field)!.invalid &&
        this.formGroupOne.get(field)!.touched &&
        this.formGroupOne.get(field)!.dirty
      );
    } else if (form === 2) {
      return (
        this.formGroupTwo.get(field)!.invalid &&
        this.formGroupTwo.get(field)!.touched &&
        this.formGroupTwo.get(field)!.dirty
      );
    }
    return true;
  }

  onlyNumber(event: any) {
    const key = event.key;
    return /^[0-9]$/.test(key);
  }

  onlyText(event: any) {
    const key = event.key;
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]$/;
    return regex.test(key);
  }

  goStepTwo() {
    this.clearLocalStorage();
    this.goToStep(2);
  }
}
